// @ts-nocheck
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, useTheme } from '@mui/material';
import { ReactComponent as AccountTreeIcon } from '../assets/Icons/lucideIcons/git-fork.svg';
import { ReactComponent as UseCaseIcon } from '../assets/Icons/lucideIcons/users.svg';

import { ReactComponent as ModelIcon } from '../assets/images/ModelDetails/ModelLogo.svg';
import { ReactComponent as ArtifactIcon } from '../assets/Icons/ArtifactsIcon.svg';
import { ReactComponent as ModelRequestIcon } from '../assets/Icons/ModelRequestIcon.svg';
import { ReactComponent as QueryIcon } from '../assets/Icons/Query 3.svg';

export const PREDEFINED_ICON_CONFIG = {
  Model: {
    colorPath: 'landscape.model',
    bgPath: 'landscape.bg.model',
    Icon: ModelIcon,
  },
  'Sub-Process': {
    colorPath: 'landscape.artifact',
    bgPath: 'landscape.bg.artifact',
    Icon: ArtifactIcon,
  },
  Assessment: {
    colorPath: 'landscape.assessment',
    bgPath: 'landscape.bg.assessment',
    Icon: ModelRequestIcon,
  },
  'Use Case': {
    colorPath: 'landscape.useCase',
    bgPath: 'landscape.bg.useCase',
    Icon: UseCaseIcon,
  },
  Query: {
    colorPath: 'landscape.query',
    bgPath: 'landscape.bg.query',
    Icon: QueryIcon,
  },
};

const DEFAULT_ICON_CONFIG = {
  colorPath: 'landscape.artifact',
  bgPath: 'landscape.bg.artifact',
  Icon: AccountTreeIcon,
};

/**
 * Utility function to safely resolve a nested property path
 * within the given Material-UI theme palette object.
 */
function resolveThemePath(palette, path) {
  return path.split('.').reduce((obj, key) => obj?.[key], palette);
}

/**
 * Renders a dynamic entity icon that flexibly supports both theme-aware
 * predefined icons and custom base64 encoded images based on the theme.
 */
const DynamicEntityIcon = ({
  icon_type = 'predefined',
  icon_name,
  light_icon,
  dark_icon,
  size = 16,
  wrapperSize = 24,
  showBackground = true,
}) => {
  const theme = useTheme();
  const isDark = theme?.palette?.mode === 'dark';

  const { color, backgroundColor, IconComponent } = useMemo(() => {
    if (icon_type === 'custom') return {};
    const config = PREDEFINED_ICON_CONFIG[icon_name] || DEFAULT_ICON_CONFIG;
    return {
      color: resolveThemePath(theme?.palette, config.colorPath),
      backgroundColor: resolveThemePath(theme?.palette, config.bgPath),
      IconComponent: config.Icon,
    };
  }, [icon_type, icon_name, theme]);

  // Render custom icon if icon_type is custom
  if (icon_type === 'custom') {
    const src = isDark ? dark_icon : light_icon;
    return (
      <Box
        sx={[
          {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '100%',
            width: wrapperSize,
            height: wrapperSize,
            flexShrink: 0,
          },
          showBackground
            ? {
                backgroundColor: 'landscape.bg.accessRequest',
              }
            : {
                backgroundColor: 'transparent',
              },
        ]}
      >
        {src ? (
          <img
            src={src}
            alt="entity icon"
            style={{ width: size, height: size, display: 'block' }}
          />
        ) : null}
      </Box>
    );
  }

  // Render predefined icon if icon_type is predefined
  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '100%',
          width: wrapperSize,
          height: wrapperSize,
          flexShrink: 0,
        },
        showBackground
          ? {
              background: backgroundColor,
            }
          : {
              background: 'transparent',
            },
      ]}
    >
      <IconComponent
        style={{
          width: size,
          height: size,
          color,
        }}
      />
    </Box>
  );
};

DynamicEntityIcon.propTypes = {
  icon_type: PropTypes.oneOf(['predefined', 'custom']),
  icon_name: PropTypes.string,
  light_icon: PropTypes.string,
  dark_icon: PropTypes.string,
  size: PropTypes.number,
  wrapperSize: PropTypes.number,
  showBackground: PropTypes.bool,
};

export default DynamicEntityIcon;
